import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldOff, SearchX } from "lucide-react";
import { api } from "../lib/api";
import { track } from "../lib/track";
import { useLang } from "../lib/i18n";
import { usePublicConfig } from "../lib/config";
import { LangToggle } from "../components/LangToggle";
import { CardView } from "../components/card/CardView";

const StateScreen = ({ icon: Icon, title, body, website, testId }) => {
  const { t } = useLang();
  return (
    <div className="rise flex flex-col items-start pt-16" data-testid={testId}>
      <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-500"><Icon className="h-8 w-8" /></span>
      <h1 className="mt-8 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{title}</h1>
      <p className="mt-3 text-base leading-relaxed text-slate-600">{body}</p>
      {website && (
        <a href={website} target="_blank" rel="noopener noreferrer" data-testid="inactive-website-link"
          className="btn-press mt-8 inline-flex min-h-[54px] items-center rounded-2xl bg-slate-900 px-6 font-semibold text-white">
          {t("visit_website")}
        </a>
      )}
    </div>
  );
};

export default function PublicCard() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const config = usePublicConfig();
  const [state, setState] = useState({ status: "loading" });
  const tracked = useRef(false);

  useEffect(() => {
    api.get(`/public/cards/${slug}`)
      .then(({ data }) => {
        setState(data);
        if (data.status !== "active" || tracked.current) return;
        tracked.current = true;
        const params = new URLSearchParams(window.location.search);
        track(slug, { type: "view", source: params.get("src") === "qr" ? "qr" : "link" });
        if (params.has("src")) window.history.replaceState(null, "", window.location.pathname);
      })
      .catch(() => setState({ status: "not_found" }));
  }, [slug]);

  useEffect(() => {
    if (!state.card) return;
    const b = config?.branding || {};
    const name = lang === "ar" ? state.card.name_ar || state.card.name_en : state.card.name_en;
    const company = lang === "ar" ? b.company_name_ar || b.company_name_en : b.company_name_en;
    document.title = company ? `${name} · ${company}` : name;
  }, [state.card, config, lang]);

  const website = config?.branding?.company_website;
  return (
    <div className="min-h-screen bg-alabaster">
      <div className="mx-auto w-full max-w-md px-4 pb-16 pt-4 sm:px-6">
        <div className="mb-4 flex justify-end"><LangToggle /></div>
        {state.status === "loading" && <div className="h-80 animate-pulse rounded-[28px] bg-slate-200" data-testid="card-loading" />}
        {state.status === "active" && <CardView card={state.card} config={config} />}
        {state.status === "inactive" && (
          <StateScreen icon={ShieldOff} title={t("inactive_title")} body={t("inactive_body")} website={website} testId="card-inactive" />
        )}
        {state.status === "not_found" && (
          <StateScreen icon={SearchX} title={t("not_found_title")} body={t("not_found_body")} website={website} testId="card-not-found" />
        )}
      </div>
    </div>
  );
}
